import { loadScript } from '@/utils';

let growObject = null;
let growObjectPromise = null;
let eventCallbacks = {
  onSuccess: () => {},
  onFailure: () => {},
  onError: () => {},
  onTimeout: () => {},
  onWalletChange: () => {},
  onPaymentStart: () => {},
  onPaymentCancel: () => {}
};

const IFRAME_SELECTOR = 'iframe[name="double-checkout"]';
const GROW_SDK_LOAD_TIMEOUT_MS = 15000;

const createGrowError = (message, stage) => {
  const error = new Error(message);
  error.stage = stage;
  return error;
};

const withTimeout = (promise, timeoutMs, error) => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(error), timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
};

const loadGrowObject = async () => {
  const widgetIframe = document.querySelector(IFRAME_SELECTOR);
  if (!widgetIframe?.contentDocument?.head || !widgetIframe.contentWindow) {
    throw createGrowError('The Grow payment form is unavailable. Please try again.', 'sdk-iframe');
  }

  const iframeHead = widgetIframe.contentDocument.head;
  // These scripts are only required for Apple Pay. Do not block the main Grow wallet while they load.
  loadScript('https://meshulam.co.il/_media/js/apple_pay_sdk/sdk.min.js', { target: document.head }).catch(() => {});
  loadScript('https://meshulam.co.il/_media/js/apple_pay_sdk/sdk.min.js', { target: iframeHead }).catch(() => {});

  await withTimeout(
    loadScript('https://cdn.meshulam.co.il/sdk/gs.min.js', {
      target: iframeHead,
      isAsync: true
    }),
    GROW_SDK_LOAD_TIMEOUT_MS,
    createGrowError('The Grow payment form took too long to load. Please try again.', 'sdk-load')
  );

  if (!widgetIframe.contentWindow.growPayment) {
    throw createGrowError('The Grow payment form failed to initialize. Please try again.', 'sdk-initialize');
  }

  const environment = process.env.NODE_ENV === 'development' || window.location.hostname.includes('demo-dev') ? 'DEV' : 'PRODUCTION';
  const config = {
    environment,
    version: 1,
    events: {
      onSuccess: (response) => eventCallbacks.onSuccess(response),
      onFailure: (response) => eventCallbacks.onFailure(response),
      onError: (response) => eventCallbacks.onError(response),
      onTimeout: (response) => eventCallbacks.onTimeout(response),
      onWalletChange: (state) => eventCallbacks.onWalletChange(state),
      onPaymentStart: (response) => eventCallbacks.onPaymentStart(response),
      onPaymentCancel: (response) => eventCallbacks.onPaymentCancel(response)
    }
  };

  return {
    init: () => widgetIframe.contentWindow.growPayment.init(config),
    renderPaymentOptions: (token) => widgetIframe.contentWindow.growPayment.renderPaymentOptions(token)
  };
};

export default {
  setEventCallbacks(callbacks) {
    eventCallbacks = { ...eventCallbacks, ...callbacks };
  },

  async getGrowObject() {
    if (growObject) {
      return growObject;
    }

    if (!growObjectPromise) {
      growObjectPromise = loadGrowObject()
        .then((object) => {
          growObject = object;
          return growObject;
        })
        .catch((error) => {
          growObjectPromise = null;
          throw error;
        });
    }

    return growObjectPromise;
  }
};
